import styles from './styles.module.scss';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import React, { useEffect, useState } from 'react';
import { addAvatarRequest, deleteAvatarRequest } from '../../../helpers/api';
import { toast } from 'react-toastify';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router-dom';
import { routes } from '../../../assets/routes';
import RecoConfetti from '../../common/RecoConfetti';
import StepperView from '../../common/StepperView';
import { IconButton } from '@material-ui/core';
import StepperIndicator from '../../common/StepperIndicator';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@material-ui/icons/ArrowForwardIosRounded';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import Loader from '../../common/Loader';
import AvatarReco from '../../common/AvatarReco';
import RtlWrapper from '../../../helpers/RtlWrapper';
import { hasGivenAccess, request } from '../../../firebase';
import { useMutation } from '@tanstack/react-query';
import { sendDeviceInfoRequest } from '../Home/api';
import { useTranslation } from '../../../i18n/LanguageProvider';

const Welcome = () => {
  const { t, isRtl } = useTranslation();
  const history = useHistory();
  const sendDeviceInfoRequestMutation = useMutation((d) => {
    sendDeviceInfoRequest(d);
  });

  const [notificationAccess, setNotificationAccess] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [stepperActiveStep, setStepperActiveStep] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfettiActive, setIsConfettiActive] = useState(false);

  const addAvatarMutation = useMutation(addAvatarRequest, {
    onSuccess({ data: { avatar } }) {
      setAvatar(avatar);
      toast.success(t('welcome.avatarAdded'));
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const deleteAvatarMutation = useMutation(deleteAvatarRequest, {
    onSuccess() {
      toast.success(t('editProfile.avatarDeleted'));
      setAvatar(null);
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  useEffect(() => {
    hasGivenAccess().then((res) => {
      setNotificationAccess(res);
    });
  }, []);

  const handleAvatarChange = (event) => {
    if (deleteAvatarMutation.isLoading || addAvatarMutation.isLoading) {
      return;
    }
    if (event.target.files.length) {
      addAvatarMutation.mutate({ file: event.target.files[0] });
    }
  };

  const handleGotoEditProfile = () => {
    history.push({
      pathname: routes.EDIT,
      state: { welcomePage: true }
    });
  };

  const handleSkipThisStep = () => {
    history.push(routes.HOME);
  };

  useEffect(() => {
    let checkNotificationAccess;

    if (currentStep === 1 && !notificationAccess) {
      console.log('it is time to check notification access');
      checkNotificationAccess = setInterval(() => {
        hasGivenAccess().then((res) => {
          if (res) {
            console.log('access granted');
            // handleStepperNext()
            setNotificationAccess(true);
          }
        });
      }, 1000);
    }

    return () => {
      clearInterval(checkNotificationAccess);
    };
  });

  let steps = [];

  steps.push(
    <>
      <h1>{t('welcome.title')}</h1>
      <p className={cx(styles.welcomeText)}>
        {t('welcome.intro')}
        <a href="https://o2reco.ir/tc" className={styles.link}>
          {' '}
          {t('settings.privacy')}{' '}
        </a>
        {t('welcome.introEnd')}
      </p>
    </>
  );

  if (!notificationAccess) {
    steps.push(
      <>
        <h1>{t('welcome.notificationTitle')}</h1>
        <p className={cx(styles.welcomeText)}>
          {t('welcome.notificationText')}
        </p>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => request(sendDeviceInfoRequestMutation)}
        >
          {t('welcome.notificationButton')}
        </Button>
      </>
    );
  }

  steps.push(
    <>
      <h1>{t('welcome.profileTitle')}</h1>
      <p className={cx(styles.welcomeText)}>{t('welcome.profileText')}</p>
      <div className={styles.avatarSection}>
        <AvatarReco size={20} avatar={avatar} chooseImage />
        <div>
          <RtlWrapper>
            <Button
              variant={'contained'}
              component="label"
              color="secondary"
              startIcon={
                addAvatarMutation.isLoading ? (
                  <Loader size={15} />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              <span>
                {addAvatarMutation.isLoading
                  ? t('welcome.uploading')
                  : avatar
                  ? t('welcome.changePhoto')
                  : t('welcome.uploadPhoto')}
              </span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarChange}
                disabled={addAvatarMutation.isLoading}
              />
            </Button>
          </RtlWrapper>
          {!!avatar && (
            <IconButton
              onClick={() => deleteAvatarMutation.mutate({ id: avatar.id })}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </div>
      </div>
    </>
  );

  steps.push(
    <>
      <h1>{t('welcome.doneTitle')}</h1>
      <p className={cx(styles.welcomeText)}>{t('welcome.doneText')}</p>
      <p className={cx(styles.welcomeText)} style={{ fontWeight: 'bold' }}>
        {t('welcome.doneHint')}
      </p>
      <Button
        size="large"
        variant="contained"
        color="secondary"
        onClick={handleGotoEditProfile}
      >
        {t('welcome.goToProfile')}
      </Button>
    </>
  );

  useEffect(() => {
    if (currentStep === steps.length - 1) {
      setIsConfettiActive(true);
    }
  }, [currentStep]);

  const handleStepperNext = () => {
    if (currentStep >= steps.length - 1) {
      return;
    }
    setStepperActiveStep(currentStep + 1);
    setCurrentStep(currentStep + 1);
  };

  const handleStepperBack = () => {
    if (currentStep <= 0) {
      return;
    }
    setStepperActiveStep(currentStep - 1);
    setCurrentStep(currentStep - 1);
  };

  const handleStepChange = (e) => {
    setStepperActiveStep(e);
    setCurrentStep(e);
  };

  return (
    <div
      className={cx(
        styles.page,
        styles.full,
        styles.noPaddingTop,
        styles.welcomeContainer
      )}
    >
      <RecoConfetti isActive={isConfettiActive} />
      <Helmet>
        <title>{t('welcome.pageTitle')}</title>
      </Helmet>
      <div className={styles.welcomeActionBar}>
        {stepperActiveStep !== 0 && (
          <Button variant="text" size="small" onClick={handleSkipThisStep}>
            {t('common.skip')}
          </Button>
        )}
      </div>
      <div className={styles.welcomeContent}>
        <StepperView
          activeStep={stepperActiveStep}
          handleStepChange={handleStepChange}
          forceDisable={
            addAvatarMutation.isLoading || deleteAvatarMutation.isLoading
          }
        >
          {steps}
        </StepperView>
      </div>

      <div className={styles.welcomeActionBar}>
        <div className={styles.slideIndicator}>
          <StepperIndicator steps={steps.length} currentStep={currentStep} />
        </div>
        <RtlWrapper>
          {currentStep > 0 && (
            <Button
              variant="text"
              color="secondary"
              startIcon={
                isRtl ? (
                  <ArrowForwardIosRoundedIcon />
                ) : (
                  <ArrowBackIosRoundedIcon />
                )
              }
              onClick={handleStepperBack}
              style={{ marginInlineEnd: 'auto' }}
              disabled={
                addAvatarMutation.isLoading || deleteAvatarMutation.isLoading
              }
            >
              {t('common.previous')}
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button
              variant="text"
              color="secondary"
              endIcon={
                isRtl ? (
                  <ArrowBackIosRoundedIcon />
                ) : (
                  <ArrowForwardIosRoundedIcon />
                )
              }
              onClick={handleStepperNext}
              style={{ marginInlineStart: 'auto' }}
              disabled={
                addAvatarMutation.isLoading || deleteAvatarMutation.isLoading
              }
            >
              {t('common.next')}
            </Button>
          )}
        </RtlWrapper>
      </div>
    </div>
  );
};

export default Welcome;
